import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { typeColor, typeLabel, severityColor, formatVal } from './constants';
import { Badge, Button } from '../design';
import { useModal } from '../ModalProvider';
import WazuhAlertList from './WazuhAlertList';

const sectionTitle = { fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: 8 };
const infoCard = { background: 'var(--glass)', borderRadius: 10, padding: 12, border: '1px solid var(--glass-border)' };
const infoLabel = { fontSize: 10, letterSpacing: '0.05em', color: 'var(--text-3)' };

function MetricSeriesChart({ series, warn, crit }) {
  const data = useMemo(() => (series || []).map((p) => ({
    label: new Date(p.ts * 1000).toISOString().slice(11, 16),
    value: Number(p.value),
  })), [series]);

  if (!data || data.length === 0) {
    return <div className="muted" style={{ fontSize: 12, padding: '24px 0', textAlign: 'center' }}>No metric series available</div>;
  }

  const refs = [];
  if (warn != null && Number.isFinite(Number(warn))) refs.push({ key: 'warn', y: Number(warn), color: '#f59e0b', label: 'warn' });
  if (crit != null && Number.isFinite(Number(crit))) refs.push({ key: 'crit', y: Number(crit), color: '#ef4444', label: 'crit' });

  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} minTickGap={40} />
        <YAxis tick={{ fontSize: 10, fill: '#64748b' }} domain={['auto', 'auto']} />
        <Tooltip contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, color: '#fff' }} />
        {refs.map((r) => (
          <ReferenceLine key={r.key} y={r.y} stroke={r.color} strokeDasharray="4 4" label={{ value: r.label, fontSize: 9, fill: r.color, position: 'insideTopRight' }} />
        ))}
        <Line type="monotone" dataKey="value" stroke="#3b82f6" dot={false} strokeWidth={1.5} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function AnomalyDrawer({ anomaly, context, loading, error, onClose, onDismiss, onSelect }) {
  const modal = useModal();
  const [dismissing, setDismissing] = useState(false);
  const [aiText, setAiText] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  if (!anomaly) return null;

  const ctx = context || {};
  const info = ctx.anomaly || anomaly;
  const baseline = ctx.baseline || null;
  const sev = info.severity || anomaly.severity;

  const handleExplain = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiText(null);
    try {
      const res = await fetch(`/api/v1/anomalies/${anomaly.id}/explain`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setAiText(data.explanation || '(empty response)');
    } catch (e) {
      setAiError(e.message || 'Failed to load AI explanation');
    } finally {
      setAiLoading(false);
    }
  };

  const handleDismiss = async () => {
    const reason = await modal.prompt('Dismiss this anomaly? (optional reason)', '', 'Dismiss anomaly');
    if (reason === null || reason === false) return;
    setDismissing(true);
    try {
      await onDismiss(anomaly.id, { reason, is_false_positive: true });
      onClose();
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 150, background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div
        style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '100%', maxWidth: 576, background: 'var(--surface)', border: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', zIndex: 160, boxShadow: '-4px 0 24px rgba(0,0,0,0.3)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex between items-start" style={{ padding: 20, borderBottom: '1px solid var(--glass-border)' }}>
          <div style={{ minWidth: 0 }}>
            <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 10px', borderRadius: 999, color: '#fff', backgroundColor: typeColor(info.anomaly_type) }}>
                {typeLabel(info.anomaly_type)}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 10px', borderRadius: 999, background: 'var(--glass)', textTransform: 'capitalize' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: severityColor(sev) }} />
                <span style={{ color: 'var(--text-1)' }}>{sev}</span>
              </span>
            </div>
            <div className="mono muted" style={{ fontSize: 13 }}>
              #{info.id || anomaly.id} · {String(info.timestamp || anomaly.timestamp || '').replace('T', ' ').slice(0, 19)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {loading ? (
            <div className="flex items-center justify-center muted" style={{ padding: '64px 0' }}>Loading context...</div>
          ) : error ? (
            <div style={{ fontSize: 13, color: 'var(--danger)', padding: '32px 0', textAlign: 'center' }}>Failed to load anomaly context</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                <div style={infoCard}>
                  <div style={infoLabel}>SERVER</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {anomaly.server_name || (info.server_id ? info.server_id.slice(0, 8) : '—')}
                  </div>
                  {(info.server_ip || anomaly.server_ip) && (
                    <div className="mono muted" style={{ fontSize: 10, marginTop: 2 }}>{info.server_ip || anomaly.server_ip}</div>
                  )}
                </div>
                <div style={infoCard}>
                  <div style={infoLabel}>METRIC</div>
                  <div className="mono" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.metric_name || '—'}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
                {[
                  { label: 'ACTUAL', value: formatVal(info.actual_value) },
                  { label: 'BASELINE', value: formatVal(info.baseline_value) },
                  { label: 'WARN', value: formatVal(info.threshold_warn) },
                  { label: 'CRIT', value: formatVal(info.threshold_crit) },
                ].map((it) => (
                  <div key={it.label} style={infoCard}>
                    <div style={infoLabel}>{it.label}</div>
                    <div className="mono" style={{ fontSize: 13, color: 'var(--text-1)', marginTop: 4 }}>{it.value}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 10, padding: 12 }}>
                <h4 style={{ ...sectionTitle, color: 'var(--accent-1)' }}>WHAT HAPPENED</h4>
                <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-1)', margin: 0 }}>
                  {ctx.explanation || (loading ? 'Analyzing...' : 'No cause summary available')}
                </p>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {aiText && (
                    <div style={{ background: 'var(--glass)', borderRadius: 8, padding: 10, border: '1px solid var(--glass-border)' }}>
                      <div style={{ fontSize: 10, letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 4 }}>AI EXPLANATION</div>
                      <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-1)', margin: 0, whiteSpace: 'pre-wrap' }}>{aiText}</p>
                    </div>
                  )}
                  {aiError && (
                    <div style={{ fontSize: 12, color: 'var(--danger)' }}>{aiError}</div>
                  )}
                  <div>
                    <Button variant="ghost" size="sm" onClick={handleExplain} disabled={aiLoading} style={{ fontSize: 12 }}>
                      {aiLoading ? 'Generating…' : '✨ Explain with AI'}
                    </Button>
                  </div>
                </div>
              </div>

              {info.score != null && (
                <div className="flex items-center gap-3" style={{ fontSize: 13 }}>
                  <span className="muted">Anomaly score</span>
                  <span className="mono" style={{ fontWeight: 600, color: severityColor(sev) }}>{formatVal(info.score)}</span>
                </div>
              )}

              <div>
                <h4 style={sectionTitle}>METRIC SERIES (24h)</h4>
                <MetricSeriesChart series={ctx.metric_series} warn={info.threshold_warn} crit={info.threshold_crit} />
              </div>

              {baseline && (
                <div>
                  <h4 style={sectionTitle}>STATISTICAL BASELINE</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    {[
                      { label: 'MEAN', v: baseline.mean },
                      { label: 'STDDEV', v: baseline.stddev },
                      { label: 'MEDIAN', v: baseline.median },
                      { label: 'P5', v: baseline.p5 },
                      { label: 'P95', v: baseline.p95 },
                      { label: 'SAMPLES', v: baseline.sample_count },
                      { label: 'WINDOW', v: baseline.window_hours != null ? `${baseline.window_hours}h` : null },
                    ].map((b) => (
                      <div key={b.label} style={{ background: 'var(--glass)', borderRadius: 8, padding: 8, border: '1px solid var(--glass-border)' }}>
                        <div style={{ fontSize: 9, letterSpacing: '0.05em', color: 'var(--text-3)' }}>{b.label}</div>
                        <div className="mono" style={{ fontSize: 12, color: 'var(--text-1)', marginTop: 2 }}>{formatVal(b.v)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 style={sectionTitle}>CORRELATED WAZUH ALERTS</h4>
                <WazuhAlertList alerts={ctx.wazuh_alerts} emptyText="No correlated Wazuh alerts (±30 min)" />
              </div>

              <div>
                <h4 style={sectionTitle}>RECENT SIMILAR (24h)</h4>
                {(ctx.related || []).length === 0 ? (
                  <div className="muted" style={{ fontSize: 12 }}>No other similar anomalies on this server</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(ctx.related || []).map((r) => (
                      <div
                        key={r.id}
                        onClick={onSelect ? () => onSelect(r) : undefined}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                          background: 'var(--glass)', borderRadius: 8, border: '1px solid var(--glass-border)',
                          cursor: onSelect ? 'pointer' : 'default', fontSize: 12,
                        }}
                      >
                        <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: typeColor(r.anomaly_type), flexShrink: 0 }} />
                        <span style={{ color: 'var(--text-1)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {typeLabel(r.anomaly_type)}
                        </span>
                        <span className="mono muted" style={{ fontSize: 11 }}>
                          {String(r.timestamp || '').replace('T', ' ').slice(5, 16)}
                        </span>
                        <span className="mono" style={{ color: 'var(--text-2)', fontSize: 11 }}>{formatVal(r.actual_value)}</span>
                        <span style={{ fontSize: 10, textTransform: 'capitalize', color: severityColor(r.severity) }}>{r.severity}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {info.details && Object.keys(info.details).length > 0 && (
                <div>
                  <h4 style={sectionTitle}>DETAILS</h4>
                  <pre className="mono" style={{ fontSize: 10, background: 'var(--glass)', borderRadius: 10, padding: 12, border: '1px solid var(--glass-border)', overflowX: 'auto', whiteSpace: 'pre-wrap', color: 'var(--text-2)' }}>
                    {JSON.stringify(info.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex" style={{ justifyContent: 'flex-end', padding: 16, borderTop: '1px solid var(--glass-border)' }}>
          <Button variant="danger" onClick={handleDismiss} disabled={dismissing || loading}>
            {dismissing ? 'Dismissing...' : 'Dismiss as False Positive'}
          </Button>
        </div>
      </div>
    </div>
  );
}
